import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis } from 'recharts';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { ComparisonChartCard } from '@practium/features/dashboard';
import styles from './BillingConversionFunnelChart.module.css';
import { BillingConversionFunnelChartSkeleton } from './BillingConversionFunnelChartSkeleton';

export interface BillingFunnelStage {
	name: string;
	value: number;
	formattedValue: string;
}

interface BillingConversionFunnelChartProps {
	title: string;
	valueLabel: string;
	mainValue: string;
	trendValue: string;
	trendLabel: string;
	trendDirection: 'up' | 'down';
	footerText: string;
	buttonText: string;
	onButtonClick: () => void;
	stages: BillingFunnelStage[];
	emptyMessage?: string;
	isLoading?: boolean;
}

interface FunnelChartPoint {
	x: number;
	name: string;
	value: number;
	formattedValue: string;
}

interface FunnelTooltipProps {
	active?: boolean;
	payload?: Array<{ payload: FunnelChartPoint }>;
}

const FunnelTooltip = ({ active, payload }: FunnelTooltipProps) => {
	if (!active || !payload || payload.length === 0) return null;

	const point = payload[0].payload;

	return (
		<div className={styles.tooltip}>
			<span className={styles.tooltipLabel}>{point.name}</span>
			<span className={styles.tooltipValue}>
				{point.value} ({point.formattedValue})
			</span>
		</div>
	);
};

const buildChartData = (stages: BillingFunnelStage[]): FunnelChartPoint[] => {
	const lastIndex = stages.length - 1;

	return stages.map((stage, index) => ({
		x: lastIndex > 0 ? (index / lastIndex) * 100 : 0,
		name: stage.name,
		value: stage.value,
		formattedValue: stage.formattedValue,
	}));
};

const clampBadgeX = (x: number) => Math.min(96, Math.max(4, x));

export const BillingConversionFunnelChart = ({
	title,
	valueLabel,
	mainValue,
	trendValue,
	trendLabel,
	trendDirection,
	footerText,
	buttonText,
	onButtonClick,
	stages,
	emptyMessage,
	isLoading,
}: BillingConversionFunnelChartProps) => {
	const isEmpty = !stages || stages.length === 0;
	const sortedStages = isEmpty ? [] : [...stages].sort((a, b) => b.value - a.value);
	const chartData = isEmpty ? [] : buildChartData(sortedStages);
	const dividerPoints = chartData.slice(1);
	const baseValue = sortedStages[0]?.value || 1;

	return (
		<div className={styles.funnelCard}>
			<ComparisonChartCard
				title={title}
				buttonText={buttonText}
				onButtonClick={onButtonClick}
				valueLabel={valueLabel}
				mainValue={mainValue}
				trendValue={trendValue}
				trendLabel={trendLabel}
				trendDirection={trendDirection}
				footerText={footerText}
				isEmpty={isEmpty}
				emptyMessage={emptyMessage}
				isLoading={isLoading}
				skeleton={<BillingConversionFunnelChartSkeleton />}
				legend={
					<div className={styles.stagesRow}>
						{sortedStages.map((stage, index) => {
							const isFirst = index === 0;
							const isLast = index === sortedStages.length - 1;
							const positionStyle = isFirst
								? { left: 0 }
								: isLast
									? { right: 0 }
									: { left: '50%', transform: 'translateX(-50%)' };

							return (
								<div key={stage.name} className={styles.stage} style={positionStyle}>
									<div className={styles.stageLabel}>
										<span className={styles.stageName}>{stage.name}</span>
										<InfoOutlined className={styles.stageInfoIcon} />
									</div>
									<div className={styles.stageValue}>
										<span className={styles.stageDot} />
										<span className={styles.stageCount}>{stage.value}</span>
										<span className={styles.stageAmount}>({stage.formattedValue})</span>
									</div>
								</div>
							);
						})}
					</div>
				}
			>
				<div className={styles.funnelOuter}>
					<ResponsiveContainer width="100%" height="100%">
						<AreaChart
							data={chartData}
							stackOffset="silhouette"
							margin={{ top: 24, right: 0, left: 0, bottom: 0 }}
						>
							<defs>
								<linearGradient id="billingConversionFunnelGradient" x1="0" y1="0" x2="1" y2="0">
									<stop offset="0%" stopColor="var(--primary)" stopOpacity={0} />
									<stop offset="1%" stopColor="var(--primary)" stopOpacity={0.16} />
									<stop offset="20%" stopColor="var(--primary)" stopOpacity={0.36} />
									<stop offset="50%" stopColor="var(--primary)" stopOpacity={0.6} />
									<stop offset="75%" stopColor="var(--primary)" stopOpacity={1} />
								</linearGradient>
							</defs>
							<XAxis type="number" dataKey="x" domain={[0, 100]} hide />
							<Tooltip content={<FunnelTooltip />} />
							<Area
								type="basis"
								dataKey="value"
								stackId="funnel"
								stroke="none"
								fill="url(#billingConversionFunnelGradient)"
								isAnimationActive={false}
							/>
						</AreaChart>
					</ResponsiveContainer>

					<div className={styles.percentBadges}>
						{dividerPoints.map((point) => (
							<div
								key={point.name}
								className={styles.divider}
								style={{ left: `${clampBadgeX(point.x)}%` }}
							>
								<span className={styles.percentBadge}>
									{Math.floor((point.value / baseValue) * 100)}%
								</span>
								<span className={styles.dividerLine} />
							</div>
						))}
					</div>
				</div>
			</ComparisonChartCard>
		</div>
	);
};
